import { useState } from 'react'
import { MultiAssigneePicker } from './MultiAssigneePicker'
import { useCreateActionsBulk } from '../hooks/useActions'
import type { ActionPriority, EventActionCreate } from '../types'

// QuickAddActionsPanel — create several actions at once (item 10). One title
// per line; the assignees / due date / priority / department below are the
// shared defaults applied to every action created. Individual actions can be
// fine-tuned afterwards from the list. Addresses the "they all have to be made
// one at a time" complaint.

// Charlotte Hawes (dbo.Users.id = 2) — project-wide default assignee, matching
// the single-action editor.
const DEFAULT_ASSIGNEE_USER_ID = 2

interface Props {
  eventId: number
  onClose: () => void
}

export function QuickAddActionsPanel({ eventId, onClose }: Props) {
  const bulk = useCreateActionsBulk(eventId)
  const [text, setText] = useState('')
  const [assigneeIds, setAssigneeIds] = useState<number[]>([DEFAULT_ASSIGNEE_USER_ID])
  const [dueAt, setDueAt] = useState('')
  const [priority, setPriority] = useState<ActionPriority>('medium')
  const [department, setDepartment] = useState('')
  const [addToPlan, setAddToPlan] = useState(true)

  const titles = text.split('\n').map((t) => t.trim()).filter((t) => t.length > 0)

  function submit() {
    if (titles.length === 0 || bulk.isPending) return
    const bodies: EventActionCreate[] = titles.map((title) => ({
      title: title.slice(0, 200),
      department: department.trim() || null,
      assigneeIds,
      addToPlan,
      dueAt: dueAt || null,
      priority,
      status: 'open'
    }))
    bulk.mutate(bodies, { onSuccess: () => { setText(''); onClose() } })
  }

  return (
    <div className="bg-white border border-line rounded p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-base">Quick add actions</h3>
        <button
          type="button"
          onClick={onClose}
          className="text-ink-soft hover:text-ink text-lg leading-none"
          aria-label="Close quick add"
        >
          ×
        </button>
      </div>
      <p className="text-xs text-ink-soft">
        One action per line. The assignees, due date, priority and department below
        apply to all of them — fine-tune individual actions from the list afterwards.
      </p>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        autoFocus
        placeholder={'Brief the stewards\nOrder the marquee\nConfirm the caterer\n…'}
        className="w-full border border-line rounded px-3 py-2 text-sm focus:outline-none focus:border-green resize-y font-body"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MultiAssigneePicker value={assigneeIds} onChange={setAssigneeIds} disabled={bulk.isPending} />
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft block mb-1">Due date</span>
            <input
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="w-full border border-line rounded px-3 py-2 text-sm focus:outline-none focus:border-green"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft block mb-1">Priority</span>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as ActionPriority)}
              className="w-full border border-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:border-green"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
        </div>
      </div>

      <label className="block">
        <span className="text-xs uppercase tracking-wide font-semibold text-ink-soft block mb-1">Department (optional)</span>
        <input
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          maxLength={80}
          placeholder="e.g. Operations"
          className="w-full border border-line rounded px-3 py-2 text-sm focus:outline-none focus:border-green"
        />
      </label>

      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
        <input
          type="checkbox"
          checked={addToPlan}
          onChange={(e) => setAddToPlan(e.target.checked)}
          className="w-4 h-4 accent-green"
        />
        <span>Add all to the project plan <span className="text-ink-soft">(creates a Gantt task each)</span></span>
      </label>

      {bulk.isError && (
        <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-2 text-xs">
          {(bulk.error as Error).message}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <span className="text-xs text-ink-soft mr-auto">
          {titles.length} action{titles.length === 1 ? '' : 's'} ready
        </span>
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 rounded text-sm text-ink-soft hover:bg-parchment"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={titles.length === 0 || bulk.isPending}
          className="px-3 py-1.5 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 disabled:opacity-50"
        >
          {bulk.isPending ? 'Adding…' : `Add ${titles.length || ''} action${titles.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  )
}
